import { msg } from "@lit/localize";
import { createAsyncActionGroup } from "@progressive-development/pd-spa-helper";
import type { ris } from "@progressive-development/politik-model";

import type {
  GebietBoden,
  GebietZahlen,
  VergleichsZeilen,
  Wahllokale,
} from "../service/gebiet-service.js";

/**
 * Die EINZIGE eigene Abfrage des Bezirks-Profils.
 *
 * Alles andere liest die Seite aus dem, was ohnehin geladen ist: Kopf und
 * Wahlergebnis stehen im Gebiets-Verzeichnis, die Termine im Aufmacher dieses
 * Bezirks — beide gehören dem Startseiten-Store und werden von dort mitbenutzt
 * (`fireOnce` bzw. dieselbe Action). Wer von der Startseite herkommt, löst
 * damit gar keinen zusätzlichen Ladevorgang aus.
 *
 * Nur die Drucksachen gibt es nirgends vorbereitet: Der Aufmacher trägt fünf,
 * das Profil zeigt zehn UND sagt, wie viele es insgesamt sind — das ist der
 * Unterschied zur gefilterten Startseite und rechtfertigt die Abfrage.
 */
export const loadGebietDrucksachen = createAsyncActionGroup<DrucksachenAnfrage, GebietDrucksachen>(
  "LOAD_GEBIET_DRUCKSACHEN",
  {
    loadingTxt: msg("Drucksachen werden geladen…", { id: "app.gebiet.drucksachen.loading.toast" }),
    smallBackground: true,
  },
);

/**
 * Die Anfrage trägt den FILTER, nicht nur ein Gebiet.
 *
 * Die beiden Ebenen fragen verschieden: Der Bezirk über die Vereinigung seiner
 * drei Anker (`bezirk`), der Stadtteil über die Ortsangabe der Quelle
 * (`stadtteil`). Wüsste die Action nur „ein Gebiet", müsste der Effect raten,
 * welcher Anker gemeint ist — und die beiden bedeuten Verschiedenes.
 */
export interface DrucksachenAnfrage {
  /** Schlüssel des Gebiets, für das geladen wird — nur zur Zuordnung. */
  fuer: string;
  filter: ris.PaperListFilter;
}

/** Das Ergebnis trägt mit, für welches Gebiet es gilt. */
export interface GebietDrucksachen {
  /** Ohne ihn wäre ein spät eintreffendes Ergebnis nicht zuzuordnen. */
  fuer: string;
  items: ris.Paper[];
  /** Wie viele es insgesamt sind; fehlt, wenn die Zählung nichts lieferte. */
  gesamt?: number;
}

/** Wie viele Drucksachen das Profil zeigt — doppelt so viele wie der Aufmacher. */
export const ANZAHL_DRUCKSACHEN = 10;

/**
 * Die Strukturdaten — Block 6, die zweite eigene Abfrage.
 *
 * Sie trägt den GEBIETSSCHLÜSSEL ("stadtbezirk:4"), nicht den Slug: Er ist der
 * gemeinsame Join-Schlüssel über Statistik, Wahlergebnisse und Gebiete, und
 * eine Gebietsnummer allein ist nur innerhalb ihrer Ebene eindeutig.
 */
export const loadGebietZahlen = createAsyncActionGroup<string, GebietZahlen>("LOAD_GEBIET_ZAHLEN", {
  loadingTxt: msg("Strukturdaten werden geladen…", { id: "app.gebiet.zahlen.loading.toast" }),
  smallBackground: true,
});

/**
 * Die Zahlenreihen eines gewählten Vergleichsgebiets.
 *
 * Eigene Action neben `loadGebietZahlen`, weil sie einen anderen Lebenslauf
 * hat: Die eigenen Zahlen holt die Seite einmal, den Vergleich wechselt man,
 * hebt ihn auf und wählt einen neuen.
 */
export const loadVergleich = createAsyncActionGroup<string, VergleichsZeilen>("LOAD_VERGLEICH", {
  loadingTxt: msg("Vergleichsgebiet wird geladen…", { id: "app.gebiet.vergleich.loading.toast" }),
  smallBackground: true,
});

/**
 * Die Wahllokale eines Stadtteils.
 *
 * Sie trägt die amtliche STADTTEILNUMMER, nicht den Slug: Die Wahl-Gliederung
 * kennt nur Nummern, und über Namen wird nicht verknüpft.
 */
export const loadWahllokale = createAsyncActionGroup<string, Wahllokale>("LOAD_WAHLLOKALE", {
  loadingTxt: msg("Wahllokale werden geladen…", { id: "app.gebiet.wahllokale.loading.toast" }),
  smallBackground: true,
});

/**
 * Die Bodenrichtwerte eines Gebiets.
 *
 * Eigener Ladevorgang neben den Strukturdaten: andere Quelle, anderer
 * Stichtag, eigener Vorbehalt — und er darf für sich scheitern, ohne die
 * Strukturdaten mitzunehmen.
 */
export const loadBodenrichtwerte = createAsyncActionGroup<string, GebietBoden>(
  "LOAD_BODENRICHTWERTE",
  {
    loadingTxt: msg("Bodenrichtwerte werden geladen…", { id: "app.gebiet.boden.loading.toast" }),
    smallBackground: true,
  },
);
